import { Suspense, useState, useEffect, useRef, ReactNode } from "react";

interface LazySectionProps {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: string;
}

/**
 * Defers rendering of children until the placeholder scrolls near the viewport.
 * This prevents React from downloading lazy-loaded chunks for off-screen sections,
 * reducing initial JS parse/execute time and improving TTI.
 */
export default function LazySection({
  children,
  rootMargin = "1200px",
  minHeight = "360px",
}: LazySectionProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref}>
      {visible ? (
        <Suspense fallback={null}>{children}</Suspense>
      ) : (
        <div className="relative overflow-hidden" style={{ minHeight }} aria-hidden>
          <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-border/30 to-transparent" />
        </div>
      )}
    </div>
  );
}